import { useEffect, useId, useRef, useState } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Switch, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { ServerError } from '@/types/errors/server.error';
import type {
  PlayerDevice,
  PlayerDeviceInventory,
  PlayerDeviceSettings,
  PlayerDto,
} from '@/types/dtos/player/response/player.dto';
import { useUpdatePlayerDeviceSettingsMutation } from '@/state/mutations/players/use-update-player-device-settings';
import { deviceControlsForPlayer, deviceControlsVisible, type DeviceControlSet } from './device-controls-for-player';

type PlayerDeviceSettingsProps = {
  player: PlayerDto;
  videoPlayerId?: string | null;
  audioPlayerId?: string | null;
  /** Speaker monitor or audience cam that should expose its audience camera. */
  audienceCameraPlayerId?: string | null;
};

export function PlayerDeviceSettings({
  player,
  videoPlayerId,
  audioPlayerId,
  audienceCameraPlayerId,
}: PlayerDeviceSettingsProps) {
  const t = useTranslations();
  const controls = deviceControlsForPlayer(
    player.type,
    player.id,
    videoPlayerId,
    audioPlayerId,
    audienceCameraPlayerId,
  );
  if (!deviceControlsVisible(controls) || !player.isConnected) return null;
  if (!player.deviceInventory) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
        {t('flows.detail.configuration.devices.waiting')}
      </Typography>
    );
  }
  return <PlayerDeviceSettingsForm player={player} inventory={player.deviceInventory} controls={controls} />;
}

function deviceName(device: PlayerDevice, unnamed: string) {
  return device.label.trim() || unnamed;
}

function withSelection(
  devices: PlayerDevice[],
  selectedId: string | null,
  selectedLabel: string | null,
): PlayerDevice[] {
  if (!selectedId || devices.some((device) => device.deviceId === selectedId)) return devices;
  return [{ deviceId: selectedId, label: selectedLabel ?? '', kind: devices[0]?.kind ?? 'audioinput' }, ...devices];
}

function PlayerDeviceSettingsForm({
  player,
  inventory,
  controls,
}: {
  player: PlayerDto;
  inventory: PlayerDeviceInventory;
  controls: DeviceControlSet;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { mutateAsync } = useUpdatePlayerDeviceSettingsMutation();
  const settings = inventory.settings;
  const queue = useRef(Promise.resolve());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [override, setOverride] = useState<Partial<PlayerDeviceSettings>>({});
  const [testing, setTesting] = useState(false);
  const draft = { ...settings, ...override };

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const fail = (error: unknown) => {
    setOverride({});
    const offline = error instanceof ServerError && error.statusCode === 409;
    notification.notifyError(
      offline ? t('flows.detail.configuration.devices.offline') : t('flows.detail.configuration.devices.saveError'),
    );
  };

  const save = (patch: Partial<PlayerDeviceSettings>) => {
    setOverride((current) => ({ ...current, ...patch }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOverride({}), 4000);
    queue.current = queue.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await mutateAsync({ playerId: player.id, body: patch });
        } catch (error) {
          fail(error);
        }
      });
  };

  const pointerLabelId = useId();
  const pointerStyleLabelId = useId();
  const pointerSizeLabelId = useId();
  const unnamed = t('flows.detail.configuration.devices.unnamed');
  const namesHidden = [...inventory.cameras, ...inventory.microphones, ...inventory.audioOutputs].some(
    (device) => !device.label.trim(),
  );
  const outputs = withSelection(inventory.audioOutputs, draft.audioOutputDeviceId, draft.audioOutputLabel);

  return (
    <Stack spacing={1} sx={{ pl: 0.5, pb: 0.5 }}>
      {controls.camera ? (
        <DeviceSelect
          label={t('flows.detail.configuration.devices.camera')}
          value={draft.cameraDeviceId}
          devices={withSelection(inventory.cameras, draft.cameraDeviceId, null)}
          unnamed={unnamed}
          onChange={(deviceId) => save({ cameraDeviceId: deviceId })}
        />
      ) : null}
      {controls.microphone ? (
        <Stack spacing={0.5}>
          <DeviceSelect
            label={t('flows.detail.configuration.devices.microphone')}
            value={draft.microphoneDeviceId}
            devices={withSelection(inventory.microphones, draft.microphoneDeviceId, null)}
            unnamed={unnamed}
            onChange={(deviceId) => save({ microphoneDeviceId: deviceId })}
          />
          <ProcessingSwitch
            label={t('flows.detail.configuration.devices.echoCancellation')}
            checked={draft.echoCancellation}
            onChange={(echoCancellation) => save({ echoCancellation })}
          />
          <ProcessingSwitch
            label={t('flows.detail.configuration.devices.noiseSuppression')}
            checked={draft.noiseSuppression}
            onChange={(noiseSuppression) => save({ noiseSuppression })}
          />
          <ProcessingSwitch
            label={t('flows.detail.configuration.devices.autoGain')}
            checked={draft.autoGainControl}
            onChange={(autoGainControl) => save({ autoGainControl })}
          />
        </Stack>
      ) : null}
      {controls.audioOutput ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <DeviceSelect
            label={t('flows.detail.configuration.devices.audioOutput')}
            value={draft.audioOutputDeviceId}
            devices={outputs}
            unnamed={unnamed}
            onChange={(deviceId) =>
              save({
                audioOutputDeviceId: deviceId,
                audioOutputLabel: outputs.find((device) => device.deviceId === deviceId)?.label ?? null,
              })
            }
          />
          <Button
            size="small"
            disabled={testing}
            onClick={() => {
              setTesting(true);
              queue.current = queue.current
                .catch(() => undefined)
                .then(() => mutateAsync({ playerId: player.id, body: { testAudioOutput: true } }))
                .catch(fail)
                .finally(() => setTesting(false));
            }}
            sx={{ flexShrink: 0, textTransform: 'none' }}
          >
            {testing
              ? t('flows.detail.configuration.devices.testing')
              : t('flows.detail.configuration.devices.testSound')}
          </Button>
        </Stack>
      ) : null}
      {namesHidden ? (
        <Typography variant="caption" color="text.secondary">
          {t('flows.detail.configuration.devices.namesHidden')}
        </Typography>
      ) : null}
      {controls.pointer ? (
        <Stack spacing={1}>
          <FormControl size="small" fullWidth>
            <InputLabel id={pointerLabelId}>{t('flows.detail.configuration.devices.pointer')}</InputLabel>
            <Select
              labelId={pointerLabelId}
              label={t('flows.detail.configuration.devices.pointer')}
              value={draft.pointerInputMode}
              onChange={(event) =>
                save({ pointerInputMode: event.target.value as PlayerDeviceSettings['pointerInputMode'] })
              }
            >
              <MenuItem value="off">{t('flows.detail.configuration.devices.pointerOff')}</MenuItem>
              <MenuItem value="holdToPoint">{t('flows.detail.configuration.devices.pointerHold')}</MenuItem>
              <MenuItem value="universal">{t('flows.detail.configuration.devices.pointerUniversal')}</MenuItem>
            </Select>
          </FormControl>
          {draft.pointerInputMode !== 'off' ? (
            <Stack direction="row" spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id={pointerStyleLabelId}>{t('flows.detail.configuration.devices.pointerStyle')}</InputLabel>
                <Select
                  labelId={pointerStyleLabelId}
                  label={t('flows.detail.configuration.devices.pointerStyle')}
                  value={draft.pointerHighlightStyle}
                  onChange={(event) =>
                    save({
                      pointerHighlightStyle: event.target.value as PlayerDeviceSettings['pointerHighlightStyle'],
                    })
                  }
                >
                  <MenuItem value="laser">{t('flows.detail.configuration.devices.laser')}</MenuItem>
                  <MenuItem value="focus">{t('flows.detail.configuration.devices.focus')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id={pointerSizeLabelId}>{t('flows.detail.configuration.devices.pointerSize')}</InputLabel>
                <Select
                  labelId={pointerSizeLabelId}
                  label={t('flows.detail.configuration.devices.pointerSize')}
                  value={draft.pointerHighlightSize}
                  onChange={(event) =>
                    save({ pointerHighlightSize: event.target.value as PlayerDeviceSettings['pointerHighlightSize'] })
                  }
                >
                  <MenuItem value="small">{t('flows.detail.configuration.devices.small')}</MenuItem>
                  <MenuItem value="medium">{t('flows.detail.configuration.devices.medium')}</MenuItem>
                  <MenuItem value="large">{t('flows.detail.configuration.devices.large')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

function DeviceSelect({
  label,
  value,
  devices,
  unnamed,
  onChange,
}: {
  label: string;
  value: string | null;
  devices: PlayerDevice[];
  unnamed: string;
  onChange: (deviceId: string | null) => void;
}) {
  const t = useTranslations();
  const labelId = useId();
  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <MenuItem value="">{t('flows.detail.configuration.devices.browserDefault')}</MenuItem>
        {devices.map((device) => (
          <MenuItem key={device.deviceId} value={device.deviceId}>
            {deviceName(device, unnamed)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ProcessingSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body2">{label}</Typography>
      <Switch checked={checked} onChange={(_, next) => onChange(next)} slotProps={{ input: { 'aria-label': label } }} />
    </Stack>
  );
}
